import { useState } from 'react';
import { MapPin, ExternalLink } from 'lucide-react';

interface MapEmbedProps {
  latitude: number;
  longitude: number;
  title?: string;
  zoom?: number;
  height?: string;
  className?: string;
  darkMode?: boolean;
  showOpenButton?: boolean;
}

// Google Maps Embed API Key (besplatno - neograničeno)
// Get key from: https://console.cloud.google.com → APIs & Services → Credentials
const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';

export function MapEmbed({
  latitude,
  longitude,
  title,
  zoom = 15,
  height = '200px',
  className = '',
  darkMode = false,
  showOpenButton = true,
}: MapEmbedProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  // Google Maps Embed URL
  const embedUrl = `https://www.google.com/maps/embed/v1/place?key=${GOOGLE_MAPS_API_KEY}&q=${latitude},${longitude}&zoom=${zoom}`;

  // Google Maps link za otvaranje
  const mapsLink = `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;

  const handleLoad = () => {
    setIsLoading(false);
  };

  const handleError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  if (hasError) {
    return (
      <div
        className={`relative overflow-hidden rounded-xl ${className}`}
        style={{ height }}
      >
        <div
          className={`w-full h-full flex flex-col items-center justify-center ${
            darkMode ? 'bg-slate-800/50' : 'bg-gray-100'
          }`}
        >
          <MapPin className={`w-8 h-8 mb-2 ${darkMode ? 'text-slate-500' : 'text-gray-400'}`} />
          <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
            Mapa nije dostupna
          </p>
          <a
            href={mapsLink}
            target="_blank"
            rel="noopener noreferrer"
            className={`mt-2 text-sm font-medium ${
              darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-500'
            }`}
          >
            Otvori u Google Maps →
          </a>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`relative overflow-hidden rounded-xl ${className}`}
      style={{ height }}
    >
      {/* Loading skeleton */}
      {isLoading && (
        <div
          className={`absolute inset-0 ${
            darkMode ? 'bg-slate-800/50' : 'bg-gray-100'
          } animate-pulse flex items-center justify-center`}
        >
          <MapPin className={`w-8 h-8 ${darkMode ? 'text-slate-600' : 'text-gray-300'}`} />
        </div>
      )}

      {/* Google Maps Embed iframe */}
      <iframe
        src={embedUrl}
        width="100%"
        height="100%"
        style={{ border: 0 }}
        allowFullScreen
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        title={title || 'Google Maps'}
        onLoad={handleLoad}
        onError={handleError}
        className={`${isLoading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}
      />

      {/* Open in Google Maps button overlay */}
      {showOpenButton && !isLoading && (
        <a
          href={mapsLink}
          target="_blank"
          rel="noopener noreferrer"
          className={`absolute bottom-2 right-2 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
            darkMode
              ? 'bg-slate-900/80 text-white hover:bg-slate-800/90 backdrop-blur-sm'
              : 'bg-white/90 text-gray-700 hover:bg-white shadow-md backdrop-blur-sm'
          }`}
        >
          <ExternalLink className="w-3 h-3" />
          <span>Google Maps</span>
        </a>
      )}
    </div>
  );
}

export default MapEmbed;
